import { Omit } from '../../common';

export enum ContentType {
    ApplicationOctetStream = 'application/octet-stream',
    ImageJPEG = 'image/jpeg',
}

export enum Extension {
    Ico = 'ico',
    JPEG = 'jpeg',
    Jpg = 'jpg',
}

export enum Four51FileType {
    Other = 'Other',
}

export enum SourceType {
    User = 'User',
}

export enum UploadInstructions {
    Empty = '*',
    None = 'none',
    UploadInstructionsNone = 'None',
}

export enum AllowedEXT {
    Empty = '*',
    PNGJpg = 'png,jpg',
}

export enum ControlType {
    File = 'File',
    Text = 'Text',
}

export interface Four51DateFormat {
    short: string;
    long: string;
}

export type UserType = 'Admin' | 'Customer' | 'Distributor' | 'Supplier' | 'TempCustomer';

export interface Four51FailureResponse {
    Message: string;
}

export interface Four51User {
    ConvertFromTempUser: boolean;
    ID: string;
    Username: string; // Available for write when creating new user from anonymous user.
    InteropID: string;
    Password: null; // Only necessary for write when changing password.
    CurrentPassword: null; // Must be identical to password.
    NewPassword: null;
    ConfirmPassword: null;
    PasswordResetToken: null;
    FirstName: string;
    LastName: string;
    Email: string;
    Phone: string | null;
    Active: boolean;
    CompanyID: string;
    Company: Company;
    AcceptConditions: boolean; // Can only be set to true. Cannot be set to false
    TermsAccepted: string;
    LastLogon: string;
    CurrentOrderID: string | null;
    PasswordExprirationDate: Date | null;
    Culture: Culture;
    CultureUI: string;
    TimeZone: TimeZone;
    Type: UserType; // Supplier, Distributor, Admin, Customer, TempCustomer
    CustomFields: CustomField[]; // To change set the 'Value' property on this object
    Permissions: string[];
    CostCenters: any[];
    ShipMethod: ShipMethod;
    AvailableCreditCards: AvailableCreditCard[];
    Groups: Group[];
}

export interface CreditCard {
    Type: string;
    EncodedAccountNumber: null;
    AccountNumber: null;
    CardholderName: null;
    DisplayName: string;
    ExpirationDate: string | null; // https://four51.github.io/#/docs/creditcard
    CVN: null;
    Save: boolean;
}

export type AvailableCreditCard = CreditCard

export interface Company {
    Name: string;
    POIDMask: string;
    TaxMethod: string;
    MerchantAccount: null;
    LogoUrl: string;
    ProductViewName: string;
    GoogleAnalyticsCode: string;
    BillZeroPriceOrders: boolean;
}

export interface Culture {
    Name: string;
    CurrencyCode: string;
    DefaultCountry: string;
}

export interface CustomField {
    DefaultValue?: string;
    Lines?: number;
    Width?: number;
    MaxLength?: number;
    MaskedInput?: string;
    ControlType: ControlType;
    ID: string;
    ListOrder: number;
    Name: string;
    Label: string;
    Value: null;
    Required: boolean;
    DisplayToUser: boolean;
    SourceType: SourceType;
    FileType?: Four51FileType;
    AllowedExt?: AllowedEXT;
    MinSize?: number;
    MaxSize?: number;
    MinHeight?: number;
    MaxHeight?: number;
    MinWidth?: number;
    MaxWidth?: number;
    MinDPI?: number;
    MaxDPI?: number;
    PreviewReduction?: number;
    UploadInstructions?: UploadInstructions;
    DefaultFileID?: string;
    File?: File;
}

export interface File {
    ID: string;
    OriginalName: string;
    Extension: Extension;
    Url: string;
    FileSize: number;
    IsImage: boolean;
    ImageWidth: number;
    ImageHeight: number;
    DotsPerInch: number;
    ContentType: ContentType;
    Scale: number;
    PageCount: number;
}

export interface Group {
    ID: string;
    InteropID: string;
    Name: string;
    Description: string;
    IsApprovalGroup: boolean;
    IsReportingGroup: boolean;
}

export interface ShipMethod {
    Name: string;
    AskForAccountNumber: boolean;
    HandlingValue: number;
    ShipperSelectionType: string;
    HandlingType: string;
    DefaultShipperAccountNumber: string;
}

export interface TimeZone {
    Name: string;
    DisplayName: string;
    Index: number;
    StandardName: string;
    DaylightName: string;
    Bias: number;
    StandardBias: number;
    DaylightBias: number;
}

export interface Product {
    Variants: any[];
    Specs: ProductSpecs;
    UnitOfMeasure: string;
    UNSPSC: string;
    ShowSpecsWithVariantList: boolean;
    BuyerDownloadsProductionJob: boolean;
    CanCreateEditRemoveVariants: boolean;
    InteropID: string;
    Type: string;
    Name: string;
    ExternalID: string;
    ViewName: string;
    Description: string;
    LargeImageUrl: string;
    SmallImageUrl: string;
    StandardPriceSchedule: StandardPriceSchedule | null;
    ReplenishmentPriceSchedule: null;
    StaticSpecGroups: StaticSpecGroups;
    VariantCount: number;
    SpecCount: number;
    QuantityMultiplier: number;
    QuantityAvailable: number;
    AllowStdOrders: boolean;
    IsVariantLevelInventory: boolean;
    IsVBOSS: boolean;
    DisplayInventory: boolean;
    AllowExceedInventory: boolean;
    InventoryEnabled: boolean;
    RelatedProductsGroupID: null;
    ShipWeight: number;
}

export interface ProductSpecs {
    DigitalStackFileURL: SpecEntry;
    URL: SpecEntry;
}

export interface SpecEntry {
    Lines: number;
    Width: number;
    MaxLength: number;
    Toolbar: boolean;
    ControlType: string;
    ID: string;
    ListOrder: number;
    Name: string;
    Label: string;
    DefaultValue: string;
    Value: string;
    Required: boolean;
    Prefix: string;
    Suffix: string;
    CanSetForLineItem: boolean;
    SourceType: string;
    SourceID: string;
    VariantAvailability: string;
    LineItemAvailability: string;
}

export type NewSpecEntry = Partial<SpecEntry> & {
    Value: string;
};

export interface PriceSchedule {
    Name: string;
    ProductID: string;
    OrderType: string;
    PriceBreaks: PriceBreak[];
    RestrictedQuantity: boolean;
    MinQuantity: number;
    MaxQuantity: number | null;
    QuantityMultiplier: number;
    UseCumulativeQuantity: boolean;
    IsKitSurcharge: boolean;
    HasNonZeroBreaks: boolean;
    DefaultQuantity: number;
}

export type StandardPriceSchedule = PriceSchedule

export interface PriceBreak {
    Quantity: number;
    Price: number;
    QuantityRangeText: string;
}

export interface ProductZoom {
    Name: string;
    Specs: ProductZoomSpec[];
}

export interface ProductZoomSpec {
    Name: string;
    Value: string;
    FileURL: null;
    VisibleToCustomer: boolean;
}

export interface OrderMetadata {
    Count: number;
    List: never[];
}

export interface Order {
    ID: string;
    ExternalID: null;
    Status: 'AwaitingApproval' | 'Canceled' | 'Completed' | 'Declined' | 'Open' | 'Unsubmitted';
    IncomingStatus: string;
    Type: string;
    FromUserID: string;
    Comments: null;
    PaymentMethod: string;
    PaymentMethodText: string;
    CreditCardID: null;
    BillAddressID: null;
    ShipAddressID: null;
    Shipper: null;
    ShipperID: null;
    TaxCost: number;
    ShippingCost: number;
    AmountOnAccount: number;
    StatusText: string;
    DateCompleted: null;
    DateSubmitted: null;
    DateCreated: string;
    DateDeclined: null;
    DateCanceled: null;
    DateSubmittedForApproval: null;
    FromUserFirstName: string;
    FromUserLastName: string;
    HasShipments: boolean;
    BillFirstName: null;
    BillLastName: null;
    BudgetAccountID: null;
    AccountName: null;
    AccountLabel: null;
    FavoriteName: string;
    Subtotal: number;
    Total: number;
    CreditCard: null;
    CostCenter: null;
    LineItems: LineItem[];
    BillAddress: null;
    ShipAddress: null;
    OrderFields: OrderField[];
    ExternalOrderDetailRecipients: null;
    RequireCVN: boolean;
    Approvals: any[];
    ApprovalComment: null;
    UserCanApprove: boolean;
    Coupon: null;
    RepeatError: null;
    ActualRatesShipperID: string;
}

export type CreateOrderRequest = Pick<Order, 'Type'> & {
    LineItems: NewLineItem[];
};

export type UpdateOrderRequest = Pick<Order, 'ID'> & {
    LineItems: NewLineItem[];
};

export interface OrderField {
    DefaultValue: string;
    Lines: number;
    Width: number;
    MaxLength: number;
    MaskedInput: string;
    ControlType: string;
    ID: string;
    ListOrder: number;
    Name: string;
    Label: string;
    Required: boolean;
    IsLineItemLevel: boolean;
    Value: string;
}

export type SimpleProduct = Pick<Product, 'InteropID'>;

export interface LineItem {
    ID: string;
    LineTotal: number;
    Product: Product;
    UnitPrice: number;
    PriceSchedule: null;
    Variant: null;
    Status: 'Completed' | 'Open';
    IsAutoForward: boolean;
    ShipAddressID: null;
    Quantity: number;
    TotalShipped: number;
    QuanitySetBySpec: boolean;
    CostCenter: null;
    DateNeeded: null;
    ShipperID: null;
    Shipper: null;
    ShipperName: null;
    ShipAccount: null;
    ShipFirstName: null;
    ShipLastName: null;
    Specs: LineItemSpecs;
    IsOpen: boolean;
    ArchivedOnly: boolean;
    ProductIDText: string;
    HistoryCaptured: boolean;
    ParentKitLineItemID: null;
    NextKitLineItem: null;
    KitItemID: null;
    QuantityPerKit: number;
    UnitPriceWithKitItems: number;
    IsConfigurable: boolean;
    IsConfigured: boolean;
    IsKitParent: boolean;
    IsKitChild: boolean;
    ProofUrl: null;
    ProductionURL: null;
    ReorderWithEdit: boolean;
}

export type NewLineItem = Omit<Partial<LineItem>, 'Product' | 'Specs'> & {
    Product: SimpleProduct;
    Specs: NewLineItemSpecs;
};

export interface StaticSpecGroups {
    ProductZoom: ProductZoom;
    VisibleSpecGroups: never[];
}

export interface LineItemSpecs {
    'Custom Field': SpecEntry;
    URL: SpecEntry;
}

export type NewLineItemSpecs = {
    [K in keyof LineItemSpecs]?: NewSpecEntry;
};
